import { isEqual } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import uuid from 'react-uuid';
import { api } from 'src/assets/static/ApiRoutes';
import useAsync from 'src/hooks/useAsync';
import {
  GlCodeFormatErrors,
  GlCodeFormatWarnings,
} from 'src/sections/checkbook_configuration/useGLCodeFormatErrors';
import axiosInstance from 'src/utils/axios';

export type GlCodeFormatType = {
  rowId: string;
  selected: boolean;
  format: string;
  isNewFormat?: boolean;
  errors?: GlCodeFormatErrors;
  warnings?: GlCodeFormatWarnings;
};

export interface GlMasksResponse {
  setGlMask: boolean;
  isManagementCompany: boolean;
  glMasks: string[];
}

const useGetGlMasks = () => {
  const [initialGlFormats, setInitialGlFormats] = useState<GlCodeFormatType[]>([]);
  const [glCodeFormats, setGlCodeFormats] = useState<GlCodeFormatType[]>([]);
  const [areFormatsRequired, setAreFormatsRequired] = useState<boolean>(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const call = useCallback(async () => {
    try {
      const res = await axiosInstance.get<GlMasksResponse>(api.getGlMasks);
      const glMasks = res.data.glMasks.map((format) => ({
        format,
        rowId: uuid(),
        selected: false,
      }));
      setInitialGlFormats(glMasks);
      setGlCodeFormats(glMasks);
      setAreFormatsRequired(res.data.setGlMask);
      setInitialLoading(false);
      return res.data;
    } catch (error) {
      throw new Error(error);
    }
  }, []);

  const { value, execute, isLoading } = useAsync(call, false);

  const areGLFormatsDirty = useMemo(
    () =>
      !isEqual(
        initialGlFormats.map((formatObject) => formatObject.format),
        glCodeFormats.map((formatObject) => formatObject.format)
      ),
    [initialGlFormats, glCodeFormats]
  );

  const isFormatRequiredFlagChanged = useMemo(
    () => areFormatsRequired !== value?.setGlMask,
    [areFormatsRequired, value?.setGlMask]
  );

  const isCheckbookConfigPageDirty = useMemo(
    () => areGLFormatsDirty || isFormatRequiredFlagChanged,
    [areGLFormatsDirty, isFormatRequiredFlagChanged]
  );

  return {
    isManagementCompany: value?.isManagementCompany,
    areGLFormatsDirty,
    glCodeFormats,
    setGlCodeFormats,
    glFormatsResponse: value,
    isFormatRequiredFlagChanged,
    isCheckbookConfigPageDirty,
    areFormatsRequired,
    setAreFormatsRequired,
    initialGlFormats,
    getGlCodeFormats: execute,
    areGLMasksLoading: isLoading,
    glMasksInitialLoading: initialLoading,
  };
};

export default useGetGlMasks;
